import {
  TimelineEntry,
  TimelineStructure,
  Integration,
} from "../../../../../../../redux/slices/projects/Types";
import { IntegrationStatusLabel } from "./StatusTag";
import moment from "moment";

/**
 * Normalizes API status values to IntegrationStatusLabel type
 */
export function normalizeStatus(
  status: string | null | undefined
): IntegrationStatusLabel {
  if (!status) return "Not applicable";

  const normalized = status.trim();
  const lower = normalized.toLowerCase();

  // Handle various case variations
  if (lower === "not started" || lower === "notstarted") return "Not started";
  if (lower === "completed") return "Completed";
  if (lower === "in progress" || lower === "inprogress") return "In progress";
  if (lower === "on hold" || lower === "onhold" || lower === "hold")
    return "On hold";
  if (lower === "not applicable" || lower === "notapplicable") return "Not applicable";
  if (lower === "validated - full approval" || lower === "validated_full_approval" || lower === "full_approval")
    return "Validated - Full approval";
  if (lower === "validated - conditional approval" || lower === "validated_conditional_approval" || lower === "conditional_approval")
    return "Validated - Conditional approval";
  if (lower === "validation - failed" || lower === "validation_failed" || lower === "failed")
    return "Validation - Failed";

  return "Not applicable";
}

/**
 * Safely accesses a nested timeline structure with case-insensitive matching
 * Filters out entries with null status (these are just for JSON structure)
 */
export function getTimelineEntries(
  timeline: TimelineStructure | undefined,
  category: string,
  subcategory?: string
): TimelineEntry[] {
  if (!timeline) return [];

  // Find category with case-insensitive matching
  const categoryKey = Object.keys(timeline).find(
    (key) => key.toLowerCase() === category.toLowerCase()
  );

  if (!categoryKey) return [];

  const categoryValue = timeline[categoryKey];

  // If it's a direct array, filter out entries with null status and return
  if (Array.isArray(categoryValue)) {
    return categoryValue.filter((entry) => entry.status !== null);
  }

  // If it's a nested object and we have a subcategory, find it
  if (
    subcategory &&
    typeof categoryValue === "object" &&
    !Array.isArray(categoryValue)
  ) {
    // Handle structure with id field: { "id": number, "Training": {...} }
    // Skip "id" field when looking for subcategories
    const subcategoryKey = Object.keys(categoryValue).find(
      (key) => key !== "id" && key.toLowerCase() === subcategory.toLowerCase()
    );

    if (subcategoryKey && Array.isArray(categoryValue[subcategoryKey])) {
      // Filter out entries with null status
      return (categoryValue[subcategoryKey] as TimelineEntry[]).filter(
        (entry) => entry.status !== null
      );
    }
  }

  return [];
}

/**
 * Maps a TimelineEntry to a format compatible with step entry types
 * Converts note (singular) to notes (plural) and formats dates
 */
export function mapTimelineEntryToStepEntry<
  T extends {
    id?: number;
    category?: string;
    start_date: string | null;
    completion_date: string | null;
    status: IntegrationStatusLabel;
    notes?: string | null;
    submitted_by?: number | null;
    submission_date?: string | null;
    created_at?: string;
  }
>(entry: TimelineEntry): T {
  // Format submission date from created_at or updated_at
  const submissionDate = entry.created_at
    ? moment(entry.created_at).format("DD/MM/YYYY")
    : entry.updated_at
    ? moment(entry.updated_at).format("DD/MM/YYYY")
    : null;

  return {
    id: entry.id,
    category: entry.category,
    start_date: entry.start_date || null,
    completion_date: entry.completion_date || null,
    status: normalizeStatus(entry.status),
    notes: entry.note || null,
    submitted_by: entry.submitted_by || null,
    submission_date: submissionDate,
    created_at: entry.created_at,
  } as T;
}

/**
 * Formats a date string from API format to display format
 * @param dateStr - The date string to format
 * @param allowBlank - If true, returns blank string for null/undefined dates instead of "-"
 */
export function formatDateForDisplay(
  dateStr: string | null | undefined,
  allowBlank: boolean = false
): string | null {
  // Handle null, undefined, or empty strings
  if (!dateStr || dateStr.trim() === "") {
    return allowBlank ? "" : "-";
  }

  // Check for invalid date patterns like "0000-00-00"
  const trimmedDate = dateStr.trim();
  if (
    trimmedDate === "0000-00-00" ||
    trimmedDate.startsWith("0000-") ||
    trimmedDate === "0000/00/00" ||
    trimmedDate === "00/00/0000"
  ) {
    return allowBlank ? "" : "-";
  }

  // Try parsing as ISO date first
  const momentDate = moment(
    dateStr,
    [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY"],
    true
  );
  
  // Check if date is valid and has a reasonable year (not 0000)
  if (momentDate.isValid() && momentDate.year() > 0) {
    return momentDate.format("DD/MM/YYYY");
  }

  // If parsing fails or year is invalid, return "-" or blank based on allowBlank
  return allowBlank ? "" : "-";
}

/**
 * Formats a Date object for database/API (YYYY-MM-DD format)
 * Uses the same pattern as formatDateForDB used elsewhere in the codebase
 */
export function formatDateForDB(date: Date | null): string {
  if (date) {
    return date.toLocaleDateString("en-CA");
  }
  return "";
}

/**
 * Gets the category ID from timeline structure for timeline create API.
 * Reads category_id from project details timeline entries (including placeholder entries).
 */
export function getCategoryId(
  timeline: TimelineStructure | undefined,
  category: string,
  subcategory?: string
): number | null {
  if (!timeline) return null;

  const categoryKey = Object.keys(timeline).find(
    (key) => key.toLowerCase() === category.toLowerCase()
  );
  if (!categoryKey) return null;

  const categoryValue = timeline[categoryKey];

  if (Array.isArray(categoryValue)) {
    const entry = categoryValue.find(
      (item) => item.category_id != null && item.category_id !== undefined
    );
    return entry?.category_id ?? null;
  }

  if (
    subcategory &&
    typeof categoryValue === "object" &&
    !Array.isArray(categoryValue)
  ) {
    const subcategoryKey = Object.keys(categoryValue).find(
      (key) =>
        key !== "ID" &&
        key !== "id" &&
        key.toLowerCase() === subcategory.toLowerCase()
    );

    if (subcategoryKey && Array.isArray(categoryValue[subcategoryKey])) {
      const entries = categoryValue[subcategoryKey] as TimelineEntry[];
      const entry = entries.find(
        (item) => item.category_id != null && item.category_id !== undefined
      );
      return entry?.category_id ?? null;
    }
  }

  return null;
}

/**
 * Gets the parent category ID from timeline structure
 * For direct categories (like "Onboarding"), returns null
 * For subcategories, extracts and returns the id from the parent category object
 */
export function getParentCategoryId(
  timeline: TimelineStructure | undefined,
  category: string,
): number | null {
  if (!timeline) return null;

  const categoryKey = Object.keys(timeline).find(
    (key) => key.toLowerCase() === category.toLowerCase()
  );
  if (!categoryKey) return null;

  const categoryValue = timeline[categoryKey];

  // If it's a direct array, no parent category ID
  if (Array.isArray(categoryValue)) {
    return null;
  }

  // If it's a nested object, check for id field
  if (typeof categoryValue === "object" && !Array.isArray(categoryValue)) {
    // Check if the object has an "id" field (parent category ID)
    if ("ID" in categoryValue && typeof categoryValue.ID === "number") {
      return categoryValue.ID;
    }
  }

  return null;
}

/**
 * Result type for getIntegrationStepStatus
 */
export type IntegrationStepStatusResult = {
  stepName: string;
  status: IntegrationStatusLabel;
  isAllComplete: boolean;
};

/**
 * Gets the current integration step status by checking each main step in order:
 * Onboarding → Training → Development → Validation
 * 
 * Returns the first step that is not "Completed" or "Not applicable".
 * If all steps are complete/not-applicable, returns Validation with isAllComplete=true.
 * 
 * For steps with no entries (null), they are treated as "In progress".
 */
export function getIntegrationStepStatus(
  integration: Integration | undefined,
  getOnboardingCurrentStatus: (integration?: Integration) => IntegrationStatusLabel | null,
  getTrainingCurrentStatus: (integration?: Integration) => IntegrationStatusLabel | null,
  getDevelopmentCurrentStatus: (integration?: Integration) => IntegrationStatusLabel | null,
  getValidationCurrentStatus: (integration?: Integration) => IntegrationStatusLabel | null
): IntegrationStepStatusResult {
  const steps: { name: string; getStatus: () => IntegrationStatusLabel | null }[] = [
    { name: "Onboarding", getStatus: () => getOnboardingCurrentStatus(integration) },
    { name: "Training", getStatus: () => getTrainingCurrentStatus(integration) },
    { name: "Development", getStatus: () => getDevelopmentCurrentStatus(integration) },
    { name: "Validation", getStatus: () => getValidationCurrentStatus(integration) },
  ];

  for (const step of steps) {
    const status = step.getStatus();
    
    // If status is null (no entries), treat as "In progress"
    if (status === null) {
      return {
        stepName: step.name,
        status: "In progress",
        isAllComplete: false,
      };
    }
    
    // If status is not "Completed" and not "Not applicable", return this step
    if (status !== "Completed" && status !== "Not applicable") {
      return {
        stepName: step.name,
        status: status,
        isAllComplete: false,
      };
    }
  }

  // All steps are complete or not applicable
  return {
    stepName: "Validation",
    status: "Completed",
    isAllComplete: true,
  };
}

/**
 * Gets the onboarding start date from the latest onboarding timeline entry
 * Returns null if no onboarding entries exist
 */
export function getOnboardingStartDate(
  integration: Integration | undefined
): string | null {
  if (!integration?.timeline) return null;

  const onboardingEntries = getTimelineEntries(integration.timeline, "Onboarding");
  if (onboardingEntries.length === 0) return null;

  // Get the latest entry (first in the array as they're sorted by date desc)
  const latestEntry = onboardingEntries[0];
  return latestEntry.start_date || null;
}
